'use client'

import * as React from 'react'

import { cn } from '@/lib/utils'

function Table({ className, ...props }: React.ComponentProps<'table'>) {
  return (
    <div data-slot="table-container" className="relative w-full overflow-x-auto">
      <table
        data-slot="table"
        className={cn('w-full caption-bottom text-sm', className)}
        {...props}
      />
    </div>
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  // Codex §7: header border-b hairline. round-4 audit: the design source's header-row specimen is
  // paper-50, which is exactly the `--background` role in light (`--background: var(--paper-50)`).
  // The semantic token keeps that light look and remaps in dark; the raw `bg-paper-50` primitive
  // painted every table header near-white in dark mode (DES-09).
  return (
    <thead
      data-slot="table-header"
      className={cn('bg-background [&_tr]:border-b', className)}
      {...props}
    />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return (
    <tbody
      data-slot="table-body"
      className={cn('[&_tr:last-child]:border-0', className)}
      {...props}
    />
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  // round-4 audit: the footer-bar specimen is paper-50, i.e. `--background` (remaps in dark);
  // specimen (was bg-muted/50 → the old, unmigrated --gray-100 primitive).
  return (
    <tfoot
      data-slot="table-footer"
      className={cn('border-t bg-background font-medium [&>tr]:last:border-b-0', className)}
      {...props}
    />
  )
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        // Codex §7: row height ~44px via min-h; divide-y hairlines; no zebra.
        // round-4 audit: hover/expanded use paper-100 (was bg-muted/50, the
        // old --gray-100 primitive) per the design source's literal row-hover
        // specimen — through the `--surface` role (`--surface: var(--paper-100)`
        // in light), since raw `bg-paper-100` has no dark value and lit a
        // hovered row near-white in dark. Selected uses bg-primary-100
        // (lime-100) — the source is explicit: "Selected row is lime-100 —
        // fill only, never an edge." In dark --primary-100 stays lime-100 (a near-white
        // fill under light text, ~1.1:1), so dark takes a lime-500 wash instead.
        'border-b transition-colors hover:bg-surface has-aria-expanded:bg-surface data-[state=selected]:bg-primary-100 dark:data-[state=selected]:bg-primary-500/15',
        className
      )}
      {...props}
    />
  )
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        // Codex §7: 11px uppercase tracked muted-foreground headers (u-label semantics); h-11 for ~44px rhythm.
        // round-4 audit: dropped `tracking-wider` — text-label already carries
        // its own 0.05em letter-spacing (matches the design source's literal
        // header spec exactly); stacking Tailwind's tracking-wider (0.025em)
        // on top risked silently overriding it depending on cascade order.
        'h-11 px-2 text-left align-middle font-medium whitespace-nowrap text-label uppercase text-muted-foreground [&:has([role=checkbox])]:pr-0',
        className
      )}
      {...props}
    />
  )
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        // Codex §7: ~44px row height via py-2.5 (rows get min ~44px with text); text-body (13px)
        'px-2 py-2.5 align-middle text-body whitespace-nowrap [&:has([role=checkbox])]:pr-0',
        className
      )}
      {...props}
    />
  )
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
  return (
    <caption
      data-slot="table-caption"
      className={cn('mt-4 text-sm text-muted-foreground', className)}
      {...props}
    />
  )
}

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption }
